import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NeedRows } from '@/components/console/NeedRows';
import { usePeopleNeeds } from '@/hooks/useConsoleData';
import { NEED_LABEL, NEED_TAGS, SOURCE_LABEL, sortNeedsUnreadFirst, type NeedTag } from '@/lib/people-data';
import { num, when } from '@/lib/format';
import s from './console.module.css';

/**
 * Needs (Brian, 2026-09-18): "find out what they need, see what people need
 * built in." Every free-text thing people typed, in one list: Something
 * wrong reports, help requests, support tickets, exercise requests, what
 * they said to Elina, reflections, cancellation reasons. Each carries a tag
 * from keyword rules (bug, request, confusion, praise) shown with a question
 * mark, because it is a suggestion and not a reading; the counts are counts
 * of suggestions. Untagged means no rule fired, not "nothing to see".
 *
 * Messages people sent us through GHL are in the same list (pv-auth #336):
 * unread ones first, each with Mark read and Reply (the person's page, with
 * the composer set to answer that message); a message from a contact with no
 * account links to the thread in GHL and has no reply button.
 *
 * On a phone the window chips and the tag counts scroll sideways in one row
 * each; the list itself already stacks. Nothing changes at 640px and above.
 */
const WINDOWS = [7, 30, 90] as const;

export default function NeedsPage() {
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const [tag, setTag] = useState<NeedTag | 'untagged' | 'all'>('all');
  const [source, setSource] = useState<string>('all');
  const [q, setQ] = useState('');
  const qc = useQueryClient();
  const needs = usePeopleNeeds(days);
  const d = needs.data;
  const sources = useMemo(() => (d ? Object.entries(d.bySource).sort((a, b) => b[1] - a[1]) : []), [d]);
  const shown = useMemo(() => {
    if (!d) return [];
    const needle = q.trim().toLowerCase();
    return sortNeedsUnreadFirst(d.rows.filter((r) => (tag === 'all' || (r.tag ?? 'untagged') === tag) && (source === 'all' || r.source === source) && (!needle || `${r.text} ${r.email ?? ''} ${r.name ?? ''}`.toLowerCase().includes(needle))));
  }, [d, tag, source, q]);
  const unread = d ? d.rows.filter((r) => r.unread === true).length : 0;

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Needs</h1>
        <span className={s.pageSub}>{d ? `${num(d.rows.length)} things typed in the last ${d.days} days${unread ? ` · ${num(unread)} unread ${unread === 1 ? 'message' : 'messages'} first` : ''} · refreshed ${when(d.generatedAt)}` : ''}</span>
      </div>

      {needs.isError && <div className={s.error}>Could not load the needs list. This page needs GET /provider/admin/people/needs.</div>}

      <div className={`${s.filters} ${s.scrollRow}`} role="tablist" aria-label="Window">
        {WINDOWS.map((w) => (
          <button key={w} type="button" role="tab" aria-selected={days === w} className={`${s.filter} ${days === w ? s.filterOn : ''}`} onClick={() => setDays(w)}>{w} days</button>
        ))}
      </div>

      {d && (
        <div className={`${s.strip} ${s.scrollRow}`} data-panel="need-counts">
          {(['all', ...NEED_TAGS, 'untagged'] as const).map((t) => (
            <button key={t} type="button" className={`${s.stat} ${tag === t ? s.personFocus : ''}`} style={{ cursor: 'pointer', textAlign: 'left' }} onClick={() => setTag(t)} aria-pressed={tag === t}>
              <span className={s.statLabel}>{t === 'all' ? 'All' : `${NEED_LABEL[t]}${t === 'untagged' ? '' : '?'}`}</span>
              <span className={s.statVal}>{t === 'all' ? num(d.rows.length) : num(d.counts[t])}</span>
              <span className={s.statSub}>{t === 'all' ? 'everything typed' : t === 'untagged' ? 'no rule fired' : 'keyword suggestion'}</span>
            </button>
          ))}
        </div>
      )}

      <div className={s.filters}>
        <input className={s.search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the texts, names, emails" aria-label="Search needs" />
        <select className={s.selectInline} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
          <option value="all">Source: all</option>
          {sources.map(([k, n]) => <option key={k} value={k}>{SOURCE_LABEL[k] ?? k} ({n})</option>)}
        </select>
      </div>

      {d && d.unavailable.length > 0 && <div className={s.jWarn}>Not read this time: {d.unavailable.map((u) => u.source).join(', ')}.</div>}

      <div className={s.told} data-panel="need-rows">
        {d && shown.length === 0 && <div className={s.empty}>Nothing matches. {d.rows.length === 0 ? 'Nobody typed anything in this window.' : ''}</div>}
        <NeedRows rows={shown} onRead={() => void qc.invalidateQueries({ queryKey: ['people-needs'] })} />
      </div>
    </div>
  );
}
